import { ChevronDown } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";

import { cn } from "../../lib/utils";

/**
 * UM jeito de recolher coisa (TUT-158 fase 1c).
 *
 * Nasceu de uma observação do dono em 05/08 que não era sobre estética: o bloco
 * de foco recolhia por uma setinha, e o cartão de canal recolhia por um botão
 * "Editar". Dois mecanismos, duas aparências e duas palavras para a mesma ação,
 * na MESMA tela. Quem está aprendendo o sistema — e no Tutelae todo mundo está,
 * porque o app é novo — paga esse imposto duas vezes.
 *
 * ## Por que `<details>` e não um botão com estado
 *
 * O elemento nativo já traz o papel de divulgação, o anúncio de
 * expandido/recolhido ao leitor de tela, o Enter e o Espaço, e a busca do
 * navegador que ABRE a seção ao encontrar texto dentro dela. Reimplementar à
 * mão é como se perde metade disso sem ninguém notar.
 *
 * (`popover-pattern.test.ts` registra este arquivo como acordeão. A trava de lá
 * proíbe `<details>` de MENU, que é outra coisa: menu precisa fechar ao clicar
 * fora e no Esc, e o `details` não faz nenhum dos dois.)
 *
 * ## O que fica visível quando está recolhido
 *
 * `summary` sempre aparece, e `whenClosed` aparece SÓ fechado. A separação
 * existe para recolher não virar esconder: o cartão de canal fechado continua
 * dizendo servidor, remetente e destinatários — o que sai da tela é o
 * formulário de edição, não a informação.
 */

function chaveDePreferencia(escopo: string) {
  return `tutelae-foco-${escopo}`;
}

/**
 * O estado de aberto/fechado, lembrado por escopo.
 *
 * Fica no `localStorage` e não no perfil: é ergonomia do navegador em uso, do
 * mesmo tamanho de "barra lateral recolhida". O `try/catch` não é zelo — o
 * `localStorage` lança em modo anônimo de alguns navegadores e em iframe com
 * cookies de terceiros bloqueados, e um acordeão não pode derrubar a tela.
 */
export function useDisclosureState(
  storageKey: string | undefined,
  defaultOpen: boolean,
): [boolean, (aberto: boolean) => void] {
  const [aberto, setAbertoInterno] = useState(() => {
    if (!storageKey) return defaultOpen;
    try {
      const guardado = window.localStorage.getItem(
        chaveDePreferencia(storageKey),
      );
      if (guardado === "aberto") return true;
      if (guardado === "recolhido") return false;
      return defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  const setAberto = useCallback(
    (proximo: boolean) => {
      setAbertoInterno(proximo);
      if (!storageKey) return;
      try {
        window.localStorage.setItem(
          chaveDePreferencia(storageKey),
          proximo ? "aberto" : "recolhido",
        );
      } catch {
        // Sem armazenamento a escolha vale só para esta sessão, e tudo bem.
      }
    },
    [storageKey],
  );

  return [aberto, setAberto];
}

type DisclosureProps = {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Sempre visível. Vira o conteúdo do `<summary>` — sem controles dentro. */
  summary: ReactNode;
  /** Só quando fechado: o resumo que impede recolher de virar esconder. */
  whenClosed?: ReactNode;
  /** Só quando aberto. */
  children: ReactNode;
  className?: string;
  /** Rótulo acessível do gatilho, quando o `summary` visível não basta. */
  toggleLabel?: string;
};

export function Disclosure({
  open,
  onOpenChange,
  summary,
  whenClosed,
  children,
  className,
  toggleLabel,
}: DisclosureProps) {
  return (
    <div className={cn("min-w-0", className)}>
      <details
        open={open}
        onToggle={(evento) => onOpenChange(evento.currentTarget.open)}
        className="group min-w-0"
      >
        <summary
          data-slot="disclosure-toggle"
          aria-label={toggleLabel}
          // TUT-282 — a altura mínima de 2,25 rem era outro valor solto. `Disclosure` é
          // acordeão de LISTA densa (24 pacotes de permissão na mesma tela,
          // painéis "operação da plataforma") na maioria dos usos medidos —
          // o token compacto é o destino. Onde o `summary` carrega um ícone
          // de 36px ao lado (cartão de canal do Atendimento), o conteúdo já
          // dita a altura mínima real; o token compacto não encolhe esse caso,
          // só evita que ele fique MAIOR sem necessidade.
          className={cn(
            "flex min-h-[var(--control-h-compact)] cursor-pointer list-none items-center justify-between gap-3 rounded-[6px]",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
            "[&::-webkit-details-marker]:hidden",
          )}
        >
          <span className="min-w-0 flex-1">{summary}</span>
          {/* A seta é o gatilho INTEIRO junto com o resumo: alvo de toque de
              uma linha, não de 16px. Ela gira para dizer o sentido — e o giro
              some sob `prefers-reduced-motion`, porque movimento decorativo não
              se impõe a quem pediu para não ter. */}
          <ChevronDown
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-[var(--muted)] transition-transform duration-[var(--dur-fast)] group-open:rotate-180 motion-reduce:transition-none"
          />
        </summary>
        <div className="mt-3 min-w-0">{children}</div>
      </details>
      {open ? null : whenClosed}
    </div>
  );
}
